// The unit board, opened from the link a unit pastes in its channel.
//
// Read on a phone, mid-fight or mid-morning, to answer four questions in one
// screen with no scroll and no tap: is there anything to do, which of our three
// sources is missing, what can the unit field, and what did the last contract
// pay me. Most opens find no contract, so the idle screen is the most-visited
// screen in the product and is designed as such.
//
// Identity here is a claim, not proof. The page asks which member you are and
// remembers it; anyone holding the link could pick any name. That is the trust
// a unit's own channel already extends, and every endpoint behind this page was
// public before the page existed. Confirmation, not permission, is the guard.
//
// What this page cannot do is read the game. Contract progress, payouts and the
// per-member split exist only if a member of the unit runs the extension, and
// when none does the board says so in plain words rather than looking broken.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type {
  MuAuctionOption,
  MuBattleOption,
  MuFeedContract,
  MuRosterMember,
  MuState,
} from "@shared/types";
import { MuFace } from "../components/MuFace";
import {
  capacityWords,
  commitmentWords,
  ledger,
  ledgerPhase,
  progressShape,
  type LedgerRow,
  type SortKey,
} from "@shared/mu/ledger";
import { MU_GEAR_SLOTS as GEAR_SLOTS } from "@shared/types";
import { battleName } from "../lib/muBattleName";
import { countryName } from "../lib/muCountryName";
import { debuffLabel, pillLabel, type Effect } from "@shared/mu/effects";
import { boardUrl, muApi, readBoardPath } from "../lib/muApi";
import { dropCachedState, readCachedState, writeCachedState } from "../lib/muCache";
import { discordMessage } from "@shared/mu/discord";
import {
  bonusSupersededLine,
  bonusSwitchBanner,
  clearBonusConfirm,
  clearTargetConfirm,
  compactDamage,
  exactDamage,
  frozenDamageParagraph,
  idleSilenceLine,
  neverRelayedParagraph,
  percentWords,
  PHASE_SENTENCE,
  PHASE_WORD,
  presetWhatIf,
  removalNotice,
  removeMemberConfirm,
  replaceBonusConfirm,
  replaceLinkConfirm,
  replaceTargetConfirm,
  setOthersCommitmentConfirm,
  TARGET_PRESETS,
  TABS,
  screenTitle,
  screenMeta,
  settledBand,
  groupHeadWords,
  subtotalWords,
  unitRowWords,
  commitWord,
  affordLine,
  presetLabel,
  signLabel,
  ownStandingLine,
  COMMIT_PRESETS,
  moneyWords,
  payoutWords,
  contractTerms,
  relayBand,
  summaryRow,
  verdictChip,
  bonusWords,
  countdownWords,
  rateWords,
  unitWideLine,
  noContractVerdict,
  WORDS,
  commitKindWords,
  type BonusAuthor,
  type TabId,
} from "@shared/mu/copy";
import { battleLink, contractCells, queueUpButton } from "@shared/mu/viewModel";
import {
  ageTone,
  ageWords,
  dataAge,
  DAMAGE_AGING_MS,
  DAMAGE_STALE_MS,
  ROSTER_AGING_MS,
  ROSTER_STALE_MS,
} from "@shared/mu/dataAge";
import { usePageTitle } from "../lib/usePageTitle";

const POLL_MS = 30000;

/** Above this many names the picker offers a filter. Below it, a filter is one
 * more control on a screen that should be a single tap. */
const PICKER_FILTER_FROM = 8;

/** How often the age line redraws between polls. Without its own clock the
 * page would keep saying "just now" through an outage, because a failed poll
 * changes no state and so redraws nothing. */
const AGE_TICK_MS = 10000;

/** A confirmation still sitting there a minute later is one somebody clicks by
 * accident. Same window the panel already uses. */
const ARM_MS = 6000;

/** Which member this browser says it is, per link, so two units shared with the
 * same person do not overwrite each other. */
function rememberedMember(token: string): string | null {
  try {
    return localStorage.getItem(`muBoardMember:${token}`);
  } catch {
    return null; // storage refused; the picker just asks again
  }
}

/** Returns whether the pick actually survived being written. Private browsing
 * and blocked storage both throw here, and one of them throws only on write,
 * so a read-back is the honest test. */
function rememberMember(token: string, userId: string): boolean {
  try {
    localStorage.setItem(`muBoardMember:${token}`, userId);
    return localStorage.getItem(`muBoardMember:${token}`) === userId;
  } catch {
    // Not remembering costs a tap per visit, nothing more — but the member
    // should be told why, rather than concluding the page is broken.
    return false;
  }
}

/** Drop the remembered pick. Saying "not me" has to survive the tab closing,
 * or it did not happen. */
function forgetMember(token: string) {
  try {
    localStorage.removeItem(`muBoardMember:${token}`);
  } catch {
    // Nothing stored, nothing to forget.
  }
}

/** Money is written the same way on both surfaces, so it comes from the shared
 * vocabulary rather than being redefined here. */
const money = moneyWords;

/** What a queued member is still good for. A pledge is a number somebody typed
 * and counts down as they deal it; a projection is recomputed from health and
 * hunger every poll and already excludes what hitting has spent. */
function stillOwed(committed: number, delivered: number, pledged: boolean): number {
  return pledged ? Math.max(0, committed - delivered) : committed;
}

interface Line {
  userId: string;
  username: string;
  committed: number;
  delivered: number;
  owed: number;
  pledged: boolean;
  /** Who put them in. The sentinel "board" means they committed from here, which
   * is neither self-queued nor a teammate's doing. */
  addedBy: string | null;
  /** What this contract pays the member, when the game has said so. */
  payout: number | null;
  /** The two things that make a row a person rather than a name: the panel has
   * shown both since avatars existed, and the board showed neither. */
  avatarUrl: string | null;
  online: boolean;
}

/** One announcement addressed to this member. A tick is enough for "the unit's
 * total moved"; it is not enough for "you were removed". */
interface Toast {
  id: string;
  text: string;
  action?: { label: string; run: () => void };
}

export default function MuBoard() {
  const { token: param = "" } = useParams();
  const path = useMemo(() => readBoardPath(param), [param]);

  // The unit id arrives in the link itself on anything minted since this
  // rewrite, so a first visit paints real numbers in one round trip instead of
  // resolving the link and then asking for state. Links already pasted into
  // channels carry only the token and take the extra trip.
  const [muId, setMuId] = useState<string | null>(path.muId);
  const [dead, setDead] = useState(false);

  const cached = useMemo(() => (path.muId ? readCachedState(path.muId) : null), [path.muId]);
  const [state, setState] = useState<MuState | null>(cached?.state ?? null);
  /** When what is on screen was stored, while it is still what was on screen at
   * open. Null once a live poll has replaced it. */
  const [cacheAge, setCacheAge] = useState<number | null>(cached?.storedAt ?? null);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<string | null>(() => rememberedMember(param));
  /** Set when a pick did not survive being written. Counting renders was wrong:
   * the page redraws on its own clock, so a first visit reached the threshold
   * within half a minute and accused a working device of losing the pick. */
  const [storageRefused, setStorageRefused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [polledAt, setPolledAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [pendingJoin, setPendingJoin] = useState<number | null>(null);
  /** What the member is typing to find themselves in the picker. */
  const [pickQuery, setPickQuery] = useState("");
  /** Which of the four screens is on. Every arrival lands on the live question,
   * and the panel does the same, from the same list of tabs. */
  const [tab, setTab] = useState<TabId>("queue");
  /** The what-if the verdict is judged against while the unit holds no
   * contract. Null means judge against the spread of real contracts. */
  const [asked, setAsked] = useState<number | null>(null);

  const previous = useRef<MuState | null>(null);
  const dismissedSwitch = useRef<string | null>(null);

  usePageTitle(state ? `${state.muName} queue` : "Unit queue");

  const pushToast = useCallback((toast: Toast) => {
    setToasts((list) => (list.some((t) => t.id === toast.id) ? list : [...list, toast]));
  }, []);

  // The token is still checked even when the unit id came free with the link.
  // Skipping it would make "replace this link" stop revoking anything, and that
  // is the one genuinely destructive action in the product. A network failure is
  // not a dead link, so only the service's own word for it counts.
  useEffect(() => {
    let live = true;
    muApi
      .resolveLink(path.token)
      .then((link) => {
        if (!live) return;
        setDead(false);
        setMuId((current) => current ?? link.muId);
      })
      .catch((err: unknown) => {
        if (!live) return;
        if (err instanceof Error && /not live/i.test(err.message)) setDead(true);
        else if (!path.muId) setError("Cannot reach the queue.");
      });
    return () => {
      live = false;
    };
  }, [path.token, path.muId]);

  const refresh = useCallback(async () => {
    if (!muId) return;
    try {
      const next = await muApi.state(muId);
      setState(next);
      setCacheAge(null);
      setPolledAt(Date.now());
      setError(null);
      writeCachedState(muId, next, Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Cannot reach the queue.");
    }
  }, [muId]);

  useEffect(() => {
    if (!muId) return;
    // A hidden tab needs no updates, and a board left open in a background tab
    // is the single biggest source of D1 read load. Poll only while visible;
    // on returning, refresh at once and resume the cadence.
    let timer: ReturnType<typeof setInterval> | undefined;
    const start = () => {
      if (timer !== undefined) return;
      timer = setInterval(() => void refresh(), POLL_MS);
    };
    const stop = () => {
      if (timer === undefined) return;
      clearInterval(timer);
      timer = undefined;
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        void refresh();
        start();
      }
    };
    if (!document.hidden) {
      void refresh();
      start();
    }
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [muId, refresh]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), AGE_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  // The battle by name, not by id, exactly as the panel names it. Decoration
  // over a link that already works, so nothing waits on it.
  //
  // Declared with the other hooks, above the early returns for "still loading"
  // and "who are you?" — a hook after a conditional return runs on some renders
  // and not others, which is React error #310 and a blank board.
  const battleId = state?.contract?.battleId ?? state?.target?.battleId ?? null;
  const [battleLabel, setBattleLabel] = useState<string | null>(null);
  /** The country that posted the focused contract, by name. An id in the strip
   * is a fact nobody can check against the game. */
  const [posterLabel, setPosterLabel] = useState<string | null>(null);
  useEffect(() => {
    if (!battleId) {
      setBattleLabel(null);
      return;
    }
    let alive = true;
    void battleName(battleId).then((name) => {
      if (alive) setBattleLabel(name);
    });
    return () => {
      alive = false;
    };
  }, [battleId]);

  const posterId =
    (state?.target ? (state.contracts?.[state.target.battleId] ?? []) : []).find(
      (o) => o.phase === "fulfilling",
    )?.poster ??
    (state?.target ? (state.contracts?.[state.target.battleId] ?? [])[0]?.poster : null) ??
    null;
  useEffect(() => {
    if (!posterId) {
      setPosterLabel(null);
      return;
    }
    let alive = true;
    void countryName(posterId).then((name) => {
      if (alive) setPosterLabel(name);
    });
    return () => {
      alive = false;
    };
  }, [posterId]);

  const act = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true);
      try {
        await fn();
      } catch (err) {
        setError(err instanceof Error ? err.message : "That did not go through.");
        return;
      } finally {
        setBusy(false);
      }
      // The control comes back the moment the WRITE lands, not when the reread
      // does. Awaiting both meant a two-second link read as four seconds of
      // dead button, for a figure the poll settles anyway. Errors from this
      // reread reach the freshness line like any other failed poll; they must
      // not be mistaken for the write having failed, because it did not.
      setError(null);
      void refresh();
    },
    [refresh],
  );

  const roster = state?.roster ?? [];
  const byId = useMemo(() => new Map(roster.map((m) => [m.userId, m])), [roster]);

  const lines = useMemo<Line[]>(() => {
    return (state?.queue ?? []).map((q) => {
      const member = byId.get(q.userId);
      const pledged = q.pledgedDamage !== null;
      const committed = q.pledgedDamage ?? member?.projectedDamage ?? 0;
      const delivered = member?.contractDamage ?? 0;
      return {
        userId: q.userId,
        username: q.username,
        committed,
        delivered,
        owed: stillOwed(committed, delivered, pledged),
        pledged,
        addedBy: q.addedBy,
        payout: member?.contractPayout ?? null,
        avatarUrl: member?.avatarUrl ?? null,
        online: member?.online ?? false,
      };
    });
  }, [state, byId]);

  // Rule 3 of the seam: anything that changed ABOUT YOU without you doing it is
  // announced in words. A row appearing or a figure moving is somebody else's
  // business and gets a tick; your own row vanishing is not.
  useEffect(() => {
    const before = previous.current;
    previous.current = state;
    if (!before || !state || !me || busy) return;

    const wasMine = before.queue.find((q) => q.userId === me);
    const isMine = state.queue.find((q) => q.userId === me);
    if (wasMine && !isMine) {
      // Left to the stored notice below, which knows who did it and why, and
      // which reaches the member who was not here when it happened.
    } else if (
      wasMine &&
      isMine &&
      wasMine.pledgedDamage !== isMine.pledgedDamage &&
      isMine.pledgedDamage !== null
    ) {
      pushToast({
        id: "changed:" + isMine.pledgedDamage,
        text: `Your commitment was set to ${exactDamage(isMine.pledgedDamage)}.`,
      });
    }

    const beforeTarget = before.contract?.confirmed ? null : (before.contract?.targetDamage ?? null);
    const afterTarget = state.contract?.confirmed ? null : (state.contract?.targetDamage ?? null);
    if (beforeTarget !== afterTarget) {
      if (afterTarget !== null && state.contract?.createdBy !== me) {
        const who = state.roster.find((m) => m.userId === state.contract?.createdBy)?.username;
        pushToast({
          id: "target:" + afterTarget,
          text: `${who ?? "Someone"} set a target of ${exactDamage(afterTarget)} on this battle. Nothing has been won.`,
        });
      } else if (afterTarget === null && beforeTarget !== null) {
        pushToast({ id: "target:cleared:" + beforeTarget, text: "The target was cleared." });
      }
    }
  }, [state, me, busy, act, pushToast]);

  // The service's own record of why this member is out of the queue, which is
  // the only thing that reaches somebody who was away when it happened. The
  // seam above can only see a removal that fell between two polls of a page
  // that was open, and the member who most needs telling did not have one.
  useEffect(() => {
    if (!state || !me || busy) return;
    const mine = state.removals?.find((r) => r.userId === me);
    if (!mine) return;
    if (state.queue.some((q) => q.userId === me)) return;
    const by = mine.removedBy
      ? (state.roster.find((m) => m.userId === mine.removedBy)?.username ?? null)
      : null;
    const notice = removalNotice(mine, by, Boolean(state.contract));
    const username = state.roster.find((m) => m.userId === me)?.username ?? "me";
    pushToast({
      // Keyed by the moment it happened, so it is said once and stays said
      // through every poll until it is dismissed.
      id: "removed:" + mine.removedAt,
      text: notice.text,
      action: {
        label: notice.rejoinLabel,
        run: () =>
          void act(() => muApi.join(state.muId, me, username, notice.rejoinAt)),
      },
    });
  }, [state, me, busy, act, pushToast]);

  if (dead) {
    return (
      <main className="board">
        <p className="board-error">This link is not live. Ask your unit for the current one.</p>
      </main>
    );
  }
  if (error && !state) {
    return (
      <main className="board">
        <p className="board-error">Cannot reach the queue. {error}</p>
        <button type="button" className="ghost" onClick={() => void refresh()}>
          Try again
        </button>
      </main>
    );
  }
  if (!state) {
    return (
      <main className="board">
        <p className="ledger-eyebrow">Reading the battlefield</p>
      </main>
    );
  }

  // Sorted by what each member can bring, which is the order the rest of the
  // board uses, and filtered by whatever they typed.
  const pickable = roster
    .filter((m) => m.username.toLowerCase().includes(pickQuery.trim().toLowerCase()))
    .slice()
    .sort((a, b) => b.projectedDamage - a.projectedDamage);

  const mine = me ? byId.get(me) : undefined;
  const inQueue = me !== null && lines.some((l) => l.userId === me);
  const owedByQueue = lines.reduce((sum, l) => sum + l.owed, 0);
  // A contract the game named is an obligation; anything else is the unit
  // planning, and the page says so rather than talking about slots nobody owes.
  const live = Boolean(state.contract?.confirmed);
  const registeredDamage = lines.reduce((sum, l) => sum + l.committed, 0);
  // The game is what names a unit, and until a roster read succeeds we do not
  // have one. Every sentence that names the unit needs a fallback, or it reads
  // "Nobody in  has."
  const unitName = state.muName || "your unit";
  const relayedAt = state.relayedAt ?? null;
  const silentMs = relayedAt === null ? null : now - relayedAt;
  const neverRelayed = relayedAt === null;
  const relaySilent = silentMs !== null && silentMs >= DAMAGE_STALE_MS;

  if (me === null) {
    return (
      <main className="board board-pick">
        <div className="board-top">
          <h1 className="board-unit">{state.muName}</h1>
        </div>
        <h2>Which one are you?</h2>
        <p>Pick your name once. This device remembers it.</p>
        {/* The only screen a web-only member sees before any numbers, so the
            standing limitation belongs here rather than buried later. */}
        <p className="board-hint">
          The unit&apos;s damage, payouts and per-member splits come from a member running the
          browser extension.{" "}
          {neverRelayed
            ? `Nobody in ${state.muName || "your unit"} does, so those stay empty here.`
            : `${state.muName || "Your unit"} has one.`}
        </p>
        {storageRefused ? (
          <p className="board-hint">
            This device is not remembering your pick. Private browsing or blocked storage will do
            that.
          </p>
        ) : null}
        {/* A flat list of two dozen names on a phone is a scroll and a squint,
            and the tap it ends in is the one mistake on this page that makes
            you somebody else. The filter appears only when the list is long
            enough to need it: on a small unit it would be furniture. */}
        {roster.length > PICKER_FILTER_FROM ? (
          <input
            className="board-pick-find"
            type="search"
            value={pickQuery}
            onChange={(e) => setPickQuery(e.target.value)}
            placeholder={`Find your name among ${roster.length}`}
            aria-label="Find your name"
            autoComplete="off"
          />
        ) : null}
        {/* The figure needs a name. A column of "1.15M" answers a question
            nobody asked; this is what each member could bring to a contract. */}
        <p className="board-hint board-pick-key">Sorted by the damage each could bring.</p>
        <ul>
          {pickable.map((m) => (
            <li key={m.userId}>
              <button
                type="button"
                onClick={() => {
                  setStorageRefused(!rememberMember(param, m.userId));
                  setMe(m.userId);
                }}
              >
                <MuFace username={m.username} avatarUrl={m.avatarUrl} online={m.online} />
                <span className="row-name">{m.username}</span>
                <span className="row-figure">{compactDamage(m.projectedDamage)}</span>
              </button>
            </li>
          ))}
        </ul>
        {pickable.length === 0 ? (
          <p className="board-hint">
            Nobody in {unitName} matches that. The list is the game&apos;s own roster, so a name
            you cannot find here is one the game does not have in this unit.
          </p>
        ) : null}
      </main>
    );
  }

  const bonusAuthor: BonusAuthor = {
    name:
      state.contract?.bonusSetBy && state.contract.bonusSetBy !== me
        ? (byId.get(state.contract.bonusSetBy)?.username ?? null)
        : null,
    setAtMs: state.contract?.bonusSetAt ? Date.parse(state.contract.bonusSetAt) : null,
    isYou: Boolean(state.contract?.bonusSetBy && state.contract.bonusSetBy === me),
  };

  // The switch from a hand-set seed to the measured fact is announced, never
  // silent. It persists until dismissed or the contract ends, because the person
  // it is addressed to may not be at the screen when it fires.
  const bonus = state.battleBonus;
  const switched =
    bonus.source === "measured" &&
    bonus.setFactor !== null &&
    bonus.measuredFactor !== null &&
    Math.abs(bonus.measuredFactor - bonus.setFactor) > 0.1;
  const switchKey = switched ? `${state.contract?.gameRef ?? state.contract?.battleId}:${bonus.setFactor}` : null;
  const showSwitch = switchKey !== null && dismissedSwitch.current !== switchKey;

  const onlineNotRegistered = roster.filter(
    (m) => m.online && !lines.some((l) => l.userId === m.userId),
  );
  const theirDamage = onlineNotRegistered.reduce((sum, m) => sum + m.projectedDamage, 0);

  const target = state.contract?.targetDamage ?? 0;
  const dealt = state.progress?.totalDamage ?? 0;
  const shortfall = Math.max(0, Math.max(0, target - dealt) - owedByQueue);

  const linkUrl = boardUrl(window.location.origin, { token: path.token, muId: state.muId });

  /** One clipboard path for both places that offer it: the pinned block on a
   * phone, and the ledger's own tools at desk width. */
  const copyForDiscord = async () => {
    const text = discordMessage(state, linkUrl);
    try {
      await navigator.clipboard.writeText(text);
      setError(null);
    } catch {
      // A refused clipboard must never surface as a raw exception. The text is
      // what the member came for, so hand it to them selectable.
      setError("Copy this into the unit channel:\n" + text);
    }
  };

  return (
    <main className="board">
      {/* One card, as both surfaces are drawn: the page is the ground behind
          it, and on a desk the card holds its own width rather than letting a
          wide screen stretch the ledger across it. */}
      <div className="board-card">
      <div className="board-top">
        <span className="board-mark">
          <Wordmark />
        </span>
        {/* The unit's name is the loud line and the tool names itself quietly
            under it, as the design system's header does. */}
        <span className="board-headnames">
          <h1 className="board-unit">{state.muName}</h1>
          <span className="board-headsub">muster · board</span>
        </span>
        {/* The battle's own bonus belongs to the battle, so every contract in it
            inherits the same figure and it is read here rather than per row. */}
        <span
          className="board-headbonus"
          data-known={state.battleBonus?.source !== "none" ? "yes" : "no"}
          title={
            state.battleBonus?.source !== "none"
              ? "The battle's own multiplier, measured from what members actually dealt. Every figure below already includes it."
              : "The game never states the stacking bonuses, and nobody has dealt enough here to measure one."
          }
        >
          {state.battleBonus && state.battleBonus.source !== "none"
            ? percentWords(state.battleBonus.factor)
            : WORDS.bonusUnknown}
        </span>
        <span className="board-phase" data-phase={state.phase}>
          {live ? PHASE_WORD[state.phase] : WORDS.noContract}
        </span>
        {/* Who this board thinks you are, next to the first thing you read.
            One mis-tap on the picker makes you somebody else — able to change
            their commitment and take them off a queue that decides who gets
            paid — and the way back used to sit below three folds, which is no
            way back at all on a phone. */}
        <button
          type="button"
          className="board-who"
          onClick={() => {
            // Clearing the React state alone left the stored pick behind: press
            // this, get distracted, close the tab, and the next visit is still
            // that member.
            forgetMember(param);
            setMe(null);
          }}
        >
          <span className="board-who-name">{mine?.username ?? "you"}</span>
          <span className="board-who-not">not you?</span>
        </button>
      </div>

      <Freshness
        now={now}
        polledAt={polledAt}
        cacheAge={cacheAge}
        rosterFetchedAt={state.rosterFetchedAt}
        damageUpdatedAt={state.progress?.updatedAt ?? null}
        relayedAt={relayedAt}
        live={live}
        error={error}
        stale={!error && polledAt !== null && now - polledAt > POLL_MS * 3}
        onRetry={() => void refresh()}
      />

      <div className="board-toasts">
        {showSwitch && bonus.setFactor !== null && bonus.measuredFactor !== null ? (
          <p className="board-toast" data-kind="notice">
            <span>{bonusSwitchBanner(bonus.setFactor, bonus.measuredFactor, bonusAuthor, now)}</span>
            <button
              type="button"
              onClick={() => {
                dismissedSwitch.current = switchKey;
                setNow(Date.now());
              }}
            >
              Dismiss
            </button>
          </p>
        ) : null}
        {toasts.map((t) => (
          <p className="board-toast" key={t.id}>
            <span>{t.text}</span>
            {t.action ? (
              <button
                type="button"
                onClick={() => {
                  t.action?.run();
                  setToasts((list) => list.filter((x) => x.id !== t.id));
                }}
              >
                {t.action.label}
              </button>
            ) : null}
            <button
              type="button"
              className="board-toast-x"
              aria-label="Dismiss"
              onClick={() => setToasts((list) => list.filter((x) => x.id !== t.id))}
            >
              &times;
            </button>
          </p>
        ))}
      </div>

      {live && relaySilent && relayedAt !== null ? (
        <p className="board-alarm">{frozenDamageParagraph(unitName, dealt, relayedAt, now)}</p>
      ) : null}

      {/* The screen's own line: what this screen is about, what is happening to
          it, and the strip that switches screens — one row, as the design draws
          it. The unit header above names the unit; this names the subject. */}
      <div className="board-screen">
        <h2 className="board-screen-name">
          {(() => {
            // The battle is named once, and its name is the way into the game:
            // a member joining from here has no other route to the fight. The
            // panel asks the same helper and gets `_self`, because it is
            // already inside the game.
            const link = battleLink({ battleId, pageBattleId: null, inGame: false });
            if (!link) return screenTitle(battleLabel);
            return (
              <a href={link.href} target={link.target} rel="noreferrer" title={link.title}>
                {screenTitle(battleLabel)} <span className="board-screen-out">&#8599;</span>
              </a>
            );
          })()}
        </h2>
        <p className="board-screen-meta">
          {screenMeta({
            phase: ledgerPhase(state),
            handSet: !state.contract?.confirmed && state.handTarget !== null,
            queueCount: lines.length,
          })}
        </p>
      <div className="board-tabs" role="tablist" aria-label="Sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            className="board-tab"
            aria-selected={tab === t.id}
            tabIndex={tab === t.id ? 0 : -1}
            onClick={() => setTab(t.id)}
            onKeyDown={(ev) => {
              // A four-tab bar that eats four tabs of keyboard travel is four
              // tabs nobody presses.
              const ids = TABS.map((x) => x.id);
              const at = ids.indexOf(tab);
              const next =
                ev.key === "ArrowRight"
                  ? ids[(at + 1) % ids.length]
                  : ev.key === "ArrowLeft"
                    ? ids[(at - 1 + ids.length) % ids.length]
                    : ev.key === "Home"
                      ? ids[0]
                      : ev.key === "End"
                        ? ids[ids.length - 1]
                        : null;
              if (!next) return;
              ev.preventDefault();
              setTab(next);
            }}
          >
            {t.id === "queue" ? `${t.label} · ${lines.length}` : t.label}
          </button>
        ))}
      </div>
      </div>



      {/* Three areas, one column on a phone and two on a desktop. The board was
          a 34rem column no matter how much screen it had: on a laptop the whole
          right half of the page was empty while the roll of who is in sat below
          the fold. The DOM order is the phone order, and the grid does the rest. */}
      <div className="board-cols" data-tab={tab}>
      {tab === "queue" ? (
      <>
      <div className="board-area-top">
      {/* A finished contract, and the one step left: filing it closes it out and
          leaves every seat, figure and pledge exactly where it was. */}
      {live && state.progress?.reached ? (
        <div className="board-settled">
          <span>
            {settledBand({
              reached: true,
              dealt: state.progress.totalDamage ?? 0,
              target: state.contract?.targetDamage ?? 0,
            })}
          </span>
          <button
            type="button"
            className="act"
            disabled={busy}
            onClick={() => void act(() => muApi.clearContract(state.muId))}
          >
            {WORDS.file}
          </button>
        </div>
      ) : null}

      {/* The contract in one strip, over the bar it is measured against: what
          the fight is, and what the paperwork on it says. Everything in it is
          read from the game, so the only control beside it is the way through
          to the screen that can change which contract this is. */}
      {(() => {
        const listed = state.target ? (state.contracts?.[state.target.battleId] ?? []) : [];
        const ours = listed.find((o) => o.phase === "fulfilling") ?? listed[0] ?? null;
        const handSet = !state.contract?.confirmed && !ours;
        const cells = contractCells({
          battle: battleLabel,
          battleName: battleLabel,
          // The name when the game will give one, the id only while it will not.
          poster: posterLabel ?? ours?.poster ?? null,
          expected:
            ours?.expectedDamage ?? state.contract?.targetDamage ?? state.handTarget ?? 0,
          ratePerK: ours?.ratePerK ?? null,
          bonusFactor:
            state.battleBonus && state.battleBonus.source !== "none"
              ? state.battleBonus.factor
              : null,
          handSet,
        });
        return (
          <div className={handSet ? "board-strip board-strip-hand" : "board-strip"}>
            {cells.map((cell) => (
              <span className="board-strip-cell" key={cell.label}>
                <span className="board-strip-label">{cell.label}</span>
                <span className={`board-strip-value board-strip-${cell.tone}`}>{cell.value}</span>
              </span>
            ))}
            <button type="button" className="board-strip-go" onClick={() => setTab("contract")}>
              change
            </button>
          </div>
        );
      })()}

      <Ledger
        state={state}
        owedByQueue={owedByQueue}
        registeredDamage={registeredDamage}
        onlineNotRegistered={onlineNotRegistered.length}
        theirDamage={theirDamage}
        relaySilent={relaySilent}
        idleSilence={
          !live && relaySilent && relayedAt !== null ? idleSilenceLine(relayedAt, now) : null
        }
        asked={asked}
        onAsk={setAsked}
      />

      {/* The one action a member outside the queue opens the board to take, and
          the whole of it: pressing it commits what their health and food afford
          and keeps following that figure. Once they are in, nothing stands here
          — their own ledger row holds the figure and the way out. A phone still
          gets the older block pinned to the bottom, which the design has not
          brought in line yet. */}
      {!inQueue ? (
        <div className="board-queue-up-wrap">
          <button
            type="button"
            className="board-queue-up"
            disabled={busy}
            title={affordLine({
              hits: Math.round(mine?.hitsCapacity ?? 0),
              perHit: mine?.damagePerHit ?? 0,
              affords: queueUpButton(mine?.projectedDamage ?? 0).capacity,
              tracking: true,
            })}
            onClick={() => {
              setPendingJoin(null);
              void act(() => muApi.join(state.muId, me, mine?.username ?? "me", null));
            }}
          >
            <span className="board-queue-up-verb">
              {queueUpButton(mine?.projectedDamage ?? 0).label}
            </span>
            <span className="board-queue-up-with">
              with {queueUpButton(mine?.projectedDamage ?? 0).with}
            </span>
          </button>
        </div>
      ) : null}


      {/* Three tiers by age, and the action only where there is one to take.
          Separate from the header's roster age on purpose. */}
      <RelayBand relayedAt={relayedAt} now={now} muName={unitName} />

      {neverRelayed ? <p className="board-alarm">{neverRelayedParagraph(unitName)}</p> : null}

      {/* The one action, under the progress it acts on and above the ledger it
          changes — where the design puts it. On a phone it is pinned to the
          bottom instead, because there it would otherwise sit a screen down. */}
      <CommitBar
        busy={busy}
        live={live}
        inQueue={inQueue}
        mine={mine}
        shortfall={shortfall}
        committed={lines.find((l) => l.userId === me)?.committed ?? 0}
        dealt={mine?.contractDamage ?? 0}
        committedIsFixed={(state.queue ?? []).some(
          (q) => q.userId === me && q.pledgedDamage !== null,
        )}
        pending={pendingJoin}
        onPending={setPendingJoin}
        onJoin={(pledge) => {
          setPendingJoin(pledge);
          void act(() => muApi.join(state.muId, me, mine?.username ?? "me", pledge)).finally(() =>
            setPendingJoin(null),
          );
        }}
        onLeave={() => void act(() => muApi.leave(state.muId, me, me))}
        onCopy={copyForDiscord}
      />

      </div>

      <aside className="board-side">
      {/* One ledger, both groups: who is in and who is still free, with the same
          rows and the same figures the panel draws as seven columns. It replaces
          the queue list and the roster screen, which answered the same question
          in two vocabularies and made one member read as two fighters. */}
      <QueueLedger
        state={state}
        me={me}
        now={now}
        busy={busy}
        onSet={(userId, username, pledge) =>
          void act(() => muApi.join(state.muId, userId, username, pledge))
        }
        onDrop={(userId) => void act(() => muApi.leave(state.muId, userId, me))}
        onCopy={copyForDiscord}
      />
      </aside>
      </>
      ) : null}

      {/* The battle and its paperwork: everything that changes the contract,
          beside the thing it changes, and off the screen that answers the live
          question. */}
      {tab === "contract" ? (
        <div className="board-area-top">
          {/* Battle first, then contract: the reader aims the unit at a fight
              and the paperwork open in it is listed underneath. */}
          <Targeting
            state={state}
            me={me}
            now={now}
            busy={busy}
            onPick={(battleId) => void act(() => muApi.setTarget(state.muId, battleId, me))}
            onHandTarget={(battleId, expectedDamage) =>
              void act(() => muApi.setHandTarget(state.muId, battleId, expectedDamage))
            }
          />

          <PlanFold
            busy={busy}
            live={live}
            me={me}
            state={state}
            bonusAuthor={bonusAuthor}
            now={now}
            onSet={(battleId, targetDamage, percent) =>
              void act(() => muApi.setContract(state.muId, me, battleId, targetDamage, percent))
            }
            onSetBonus={(percent) => void act(() => muApi.setBattleBonus(state.muId, percent, me))}
            onClear={() => void act(() => muApi.clearContract(state.muId))}
          />
        </div>
      ) : null}

      {tab === "history" ? (
        <div className="board-area-top">
          <LastContract
            muId={state.muId}
            muName={unitName}
            me={me}
            roster={roster}
            neverRelayed={neverRelayed}
            idle={!live}
          />
        </div>
      ) : null}

      {/* Set once, and never on the screen that answers the live question. */}
      {tab === "settings" ? (
        <div className="board-area-top">
          <DiscordFold
            busy={busy}
            roster={roster}
            onSet={(userId, discordId) =>
              void act(() => muApi.setDiscordId(state.muId, userId, discordId))
            }
          />

          <LinkFold muId={state.muId} muName={unitName} url={linkUrl} busy={busy} />

          {/* Never a fold: what the board cannot do is a property of the unit,
              and burying it where nobody opens it is how "states plainly"
              becomes a lie. It is never an instruction to install anything. */}
          <p className="board-standing">
            Everything here is shared with the panel. What the board cannot do is read the game:
            damage, payouts and splits arrive from a member with the extension open. The board
            cannot notify you either — only the extension can.
          </p>
        </div>
      ) : null}
      </div>
      </div>
    </main>
  );
}

/* ------------------------------------------------------------- freshness */

/**
 * How old what you are reading is, and which of the three sources is missing.
 *
 * Everything on this page is second-hand. The roster is a cached read of the
 * game; contract damage only advances while somebody in the unit has the game
 * open; and a page that cannot reach the service at all shows figures from
 * whenever it last could. Those are three different problems with three
 * different actions, so they are three different sentences.
 *
 * Quiet when everything is fine. Fresh says nothing, aging says it plainly,
 * stale says do not act on this.
 */
function Freshness({
  now,
  polledAt,
  cacheAge,
  rosterFetchedAt,
  damageUpdatedAt,
  relayedAt,
  live,
  error,
  stale,
  onRetry,
}: {
  now: number;
  polledAt: number | null;
  cacheAge: number | null;
  rosterFetchedAt: number;
  damageUpdatedAt: number | null;
  relayedAt: number | null;
  live: boolean;
  error: string | null;
  stale: boolean;
  onRetry: () => void;
}) {
  const age = dataAge(now, polledAt, rosterFetchedAt, damageUpdatedAt);
  const rosterTone = ageTone(age.rosterMs, ROSTER_AGING_MS, ROSTER_STALE_MS);
  const damageTone =
    age.damageMs === null ? "fresh" : ageTone(age.damageMs, DAMAGE_AGING_MS, DAMAGE_STALE_MS);
  const silentMs = relayedAt === null ? null : now - relayedAt;

  // A cached paint is never presented as current, and the age sits with the
  // figures rather than in a corner.
  if (cacheAge !== null) {
    return (
      <p className="board-age" data-tone="aging">
        <span className="board-age-poll">
          From your last visit, {ageWords(now - cacheAge)}. Refreshing&hellip;
        </span>
      </p>
    );
  }

  if (error) {
    return (
      <p className="board-age" data-stale="yes">
        <span className="board-age-poll" title={error}>
          {typeof navigator !== "undefined" && navigator.onLine === false
            ? "Cannot reach the queue service. This device may be offline."
            : `Cannot reach the queue. Showing ${polledAt ? new Date(polledAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "an older read"}.`}
        </span>
        <button type="button" className="board-age-retry" onClick={onRetry}>
          Try again
        </button>
      </p>
    );
  }

  const items: Array<{ tone: string; text: string }> = [];
  if (stale) items.push({ tone: "stale", text: `not updating, last heard ${ageWords(age.polledMs)}` });
  if (rosterTone !== "fresh") {
    items.push({ tone: rosterTone, text: `Health and hits from ${ageWords(age.rosterMs)}` });
  }
  // Source 2: a relaying member. Unit-scoped, so it is sayable on the idle
  // screen, where no contract-scoped clock can carry any evidence at all.
  if (silentMs !== null && silentMs >= DAMAGE_STALE_MS) {
    // While a contract is being fought the alarm paragraph below says all of
    // this at length, in red, three lines further down the same screen. Saying
    // it twice in nearly the same words does not make it twice as true; it
    // makes the reader wonder whether they are two different problems.
    if (!live) {
      items.push({ tone: "stale", text: idleSilenceLine(relayedAt!, now).split(".")[0] + "." });
    }
  } else if (age.damageMs !== null && damageTone !== "fresh") {
    items.push({ tone: damageTone, text: `Damage from ${ageWords(age.damageMs)}` });
  }

  if (items.length === 0) {
    return (
      <p className="board-age">
        <span className="board-age-poll">Live</span>
      </p>
    );
  }
  return (
    <p className="board-age">
      {items.map((item) => (
        <span key={item.text} className="board-age-item" data-tone={item.tone}>
          {item.text}
        </span>
      ))}
    </p>
  );
}

/* ---------------------------------------------------------------- ledger */

/**
 * The contract as three materials against the target: what has landed, what
 * the queue still owes, and the gap nobody has covered. Drawn rather than
 * filled for the owed part, because a promise is not damage.
 *
 * With no contract this is the underwriting answer instead, which is not "who
 * is available" but "can we cover a million". The presets are the what-if, and
 * clicking one is not a form.
 */
function Ledger({
  state,
  owedByQueue,
  registeredDamage,
  onlineNotRegistered,
  theirDamage,
  relaySilent,
  idleSilence,
  asked,
  onAsk,
}: {
  state: MuState;
  owedByQueue: number;
  registeredDamage: number;
  onlineNotRegistered: number;
  theirDamage: number;
  relaySilent: boolean;
  idleSilence: string | null;
  asked: number | null;
  onAsk: (target: number | null) => void;
}) {
  /** Whether the bar is explaining itself. Opened by hover, focus, keys or a
   * tap, so the explanation is not something only a mouse can reach. */
  const [barOpen, setBarOpen] = useState(false);
  /** The value under the pointer, as a share of the bar's own scale. Null until
   * a pointer is actually over the bar: a figure printed at a position nobody
   * is pointing at is a figure about nothing. */
  const [barAt, setBarAt] = useState<number | null>(null);

  if (!state.contract) {
    return (
      <section className="ledger">
        {/* The phase badge in the header already says what the unit holds. This
            band says what the member should do about it, and repeating the word
            here only spent a line of the four the idle screen has. */}
        <p className="ledger-sentence">{PHASE_SENTENCE.idle}</p>
        {/* The total and the verdict it earns are in the standing card above,
            which says them in every state. Repeating the figure here put the
            same fact on one screen twice in two wordings, and left the reader
            checking whether the two agreed. What is left is what the card does
            not say: how many said they are available, and how many did not. */}
        <p className="ledger-of">
          {state.queue.length} registered
          {onlineNotRegistered > 0 ? `, ${onlineNotRegistered} more online but not registered` : ""}
        </p>
        {/* A standing queue holds a real figure against nothing yet, in the
            words both surfaces use for it. It borrows the maths of an open
            auction and none of its copy. */}
        <p className="ledger-verdict-chip" data-phase="idle">
          {noContractVerdict(registeredDamage)}
        </p>
        <p className="ledger-note">{WORDS.noAuction}</p>
        {/* The verdict itself is in the standing card at the top of this
            screen, where it is said in every state rather than only in this
            one. Saying it twice, in two wordings, on one screen was the thing
            that made the reader assemble the answer themselves. */}
        <div className="ledger-presets">
          {TARGET_PRESETS.map((t) => (
            <button
              key={t}
              type="button"
              data-on={asked === t ? "yes" : undefined}
              onClick={() => onAsk(asked === t ? null : t)}
            >
              {compactDamage(t)}
            </button>
          ))}
        </div>
        {asked !== null ? (
          <p className="ledger-note">
            {presetWhatIf(registeredDamage, asked, onlineNotRegistered, theirDamage)}
          </p>
        ) : null}
        {idleSilence ? <p className="ledger-note">{idleSilence}</p> : null}
      </section>
    );
  }

  const target = state.contract.targetDamage;
  const dealt = state.progress?.totalDamage ?? 0;
  const remaining = Math.max(0, target - dealt);
  const uncovered = Math.max(0, remaining - owedByQueue);
  const planning = !state.contract.confirmed;
  // The same shape the panel draws, from the same function: it rescales once
  // the unit is past the target and keeps a marker where the target sits on the
  // new scale. Two surfaces drawing one fact two ways is the failure the shared
  // model exists to prevent.
  const shape = progressShape({ target, dealt, promised: owedByQueue, estimated: 0 });
  /** What the bar's widths are a proportion OF, which is the target until the
   * unit outgrows it. */
  const barSpan = Math.max(target, dealt + owedByQueue);
  const dealtPct = shape.dealtPct;
  const owedPct = shape.promisedPct;
  const markerPct = shape.markerPct;
  const overshot = shape.overshot;
  // Ruling 6: with more than one contract being fulfilled, coverage per
  // contract reads one pool of commitments and each can print "covered" while
  // the unit is short overall. This states the total and does not follow
  // whichever contract is on screen.
  const wide = unitWideLine(state.unitWide);
  // The one sentence the tool exists to say, in the design system's vocabulary
  // rather than one of our own. A standing queue borrows the maths of an open
  // auction and none of its copy.
  const verdict = verdictChip({
    phase: planning ? "none" : state.progress?.reached ? "completed" : "fulfilling",
    committed: owedByQueue,
    open: uncovered,
    over: Math.max(0, dealt - target),
    short: Math.max(0, target - dealt),
  });
  const summary = summaryRow({
    dealt,
    promised: owedByQueue,
    open: uncovered,
    over: Math.max(0, dealt - target),
    closed: state.progress?.reached === true,
  });

  return (
    <section className="ledger">
      {/* The design system's order: the big dealt figure, what it is of, and
          the verdict beside it. The eyebrow that used to lead here renamed the
          same figure three ways depending on phase, which is the reader doing
          the deriving. */}
      <p className="ledger-head">
        <span className="ledger-figure" title={`${exactDamage(dealt)} dealt`}>
          {compactDamage(dealt)}
        </span>
        <span className="ledger-of">of {compactDamage(target)} dealt</span>
        <span className="ledger-gap" />
        <span className="ledger-verdict-chip" data-verdict={verdict.tone}>
          {verdict.text}
        </span>
      </p>
      <p className="ledger-sentence">{PHASE_SENTENCE[state.phase]}</p>

      {/* A button, not an image with a title: a tooltip only the mouse can
          reach is a fact only some readers get. This one answers focus, keys
          and touch as well, and its detail is a real element underneath rather
          than a native tooltip. */}
      <button
        type="button"
        className="ledger-bar"
        aria-expanded={barOpen}
        aria-label={`${exactDamage(dealt)} dealt, ${exactDamage(owedByQueue)} owed by the queue, target ${exactDamage(target)}`}
        onClick={() => setBarOpen(!barOpen)}
        onFocus={() => setBarOpen(true)}
        onBlur={() => setBarOpen(false)}
        onMouseEnter={() => setBarOpen(true)}
        onMouseLeave={() => {
          setBarOpen(false);
          setBarAt(null);
        }}
        // The design's bar answers "what is this width worth" wherever the
        // pointer is, not only at its ends.
        onMouseMove={(ev) => {
          const box = ev.currentTarget.getBoundingClientRect();
          if (box.width <= 0) return;
          const share = Math.min(1, Math.max(0, (ev.clientX - box.left) / box.width));
          setBarAt(share * barSpan);
        }}
      >
        <span
          className="ledger-dealt"
          // Gold while a contract is being worked, green once it is met: the
          // role colour follows the outcome, and it is never bone.
          data-tone={state.progress?.reached ? "completed" : "live"}
          // The material rule: solid happened, hatch was promised or is merely
          // still true. Past ten minutes with nobody relaying, the dealt figure
          // is the second of those.
          data-aged={relaySilent ? "yes" : undefined}
          style={{ width: `${dealtPct}%` }}
        />
        <span className="ledger-owed" style={{ width: `${owedPct}%` }} />
        {/* Where the target sits on a bar that has outgrown it. */}
        {overshot ? (
          <span className="ledger-mark" style={{ left: `${markerPct}%` }} aria-hidden="true" />
        ) : null}
      </button>

      {/* The scale the bar is drawn on, in quarters. Without it the bar is a
          proportion of nothing: a reader can see a segment is half the width
          and not what half is. */}
      <p className="ledger-ticks" aria-hidden="true">
        <span>0</span>
        <span>{compactDamage(barSpan * 0.25)}</span>
        <span>{compactDamage(barSpan * 0.5)}</span>
        <span>{compactDamage(barSpan * 0.75)}</span>
        <span>{compactDamage(barSpan)}</span>
      </p>

      {barOpen ? (
        <p className="ledger-note" role="status">
          {barAt !== null ? <strong>{exactDamage(barAt)} here · </strong> : null}
          {exactDamage(dealt)} dealt · {exactDamage(owedByQueue)} still owed by the queue ·{" "}
          {exactDamage(target)} asked for
          {overshot ? ` · ${exactDamage(dealt - target)} past the target` : ""}
        </p>
      ) : null}

      {/* Three materials, named once under the bar that uses them: solid is a
          fact the game reported, hatched is a promise somebody made, dashed is
          our arithmetic from their health. Learned here, readable everywhere. */}
      <p className="ledger-key">
        <span className="ledger-key-item" data-material="fact">
          dealt
        </span>
        <span className="ledger-key-item" data-material="promise">
          committed
        </span>
        <span className="ledger-key-item" data-material="estimate">
          projected
        </span>
      </p>

      {/* What happened, what was promised against it, and what neither covers:
          three parts, in the order they are read. */}
      <p className="ledger-legend">
        <span>
          {summary.dealt}
          {relaySilent ? <em className="ledger-frozen"> frozen</em> : null}
        </span>
        <span>{summary.promised}</span>
        <span className={uncovered > 0 ? "short" : undefined}>{summary.open}</span>
      </p>

      {wide ? <p className="ledger-wide">{wide}</p> : null}

      {state.phase === "done" ? (
        <p className="ledger-note">
          Payouts appear here when the game splits them, usually within a few minutes.
        </p>
      ) : null}
    </section>
  );
}

/* ----------------------------------------------------------------- queue */


/* ---------------------------------------------------------------- roster */




/**
 * Wordmark 6d, the ruled sheet.
 *
 * The MU tool is Muster; WarUrna remains the app. 32x32 viewBox, an 18x26
 * outline in `currentColor` at 2.5, a gold band across the top six units, and
 * two rules — dropping to one at 16px, where the second stops being a rule and
 * starts being noise.
 */
function Wordmark() {
  return (
    <svg className="mark" viewBox="0 0 32 32" width="20" height="20" aria-hidden="true" focusable="false">
      <rect x="7" y="3" width="18" height="26" rx="1" fill="none" stroke="currentColor" strokeWidth="2.5" />
      <rect x="7" y="3" width="18" height="6" fill="#e8b23c" />
      <rect className="mark-rule" x="11" y="15" width="10" height="2.5" fill="currentColor" />
      <rect className="mark-rule mark-rule-2" x="11" y="21" width="10" height="2.5" fill="currentColor" />
    </svg>
  );
}

/* ------------------------------------------------------------ relay band */

/**
 * How old the relayed figures are, in three tiers, with the one action that
 * fixes an outage.
 *
 * Deliberately worded apart from the header's age. That one says when the
 * ROSTER was read from the game; this says how old the RELAYED damage is, and
 * wording them the same taught readers the two clocks were one. Under ten
 * minutes it is ordinary and the band stays quiet.
 */
function RelayBand({
  relayedAt,
  now,
  muName,
}: {
  relayedAt: number | null;
  now: number;
  muName: string;
}) {
  const band = relayBand(relayedAt, now, muName);
  if (band.tier === "live") return null;
  return (
    <p className="relay-band" data-tier={band.tier}>
      <span>{band.text}</span>
      {band.action === "Open the game" ? (
        <a href="https://app.warera.io/" target="_blank" rel="noreferrer">
          {band.action}
        </a>
      ) : null}
    </p>
  );
}

/* ------------------------------------------------------------- targeting */

/** One battle the unit could aim at. The bonus reads `bonus unknown` unless it
 * was measured here: the game never states the stacking bonuses, so an unfought
 * battle has no figure and never a fabricated zero. */
function BattleRow({
  battle,
  targeted,
  busy,
  onPick,
}: {
  battle: MuBattleOption;
  targeted: boolean;
  busy: boolean;
  onPick: () => void;
}) {
  const [label, setLabel] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    void battleName(battle.battleId).then((name) => {
      if (live) setLabel(name);
    });
    return () => {
      live = false;
    };
  }, [battle.battleId]);

  return (
    <li className={targeted ? "tg-battle is-on" : "tg-battle"}>
      <button type="button" className="tg-pick" disabled={busy} aria-pressed={targeted} onClick={onPick}>
        <span className="tg-battle-name">{label ?? battle.battleId}</span>
        <span className="tg-battle-meta">
          {battle.held
            ? "holding a contract here"
            : `${battle.contractCount} ${battle.contractCount === 1 ? "contract" : "contracts"}`}
          {" · "}
          {bonusWords(battle.bonusFactor)}
        </span>
      </button>
    </li>
  );
}

/** One contract on offer, with the terms a unit decides on. An auction the
 * unit's reputation cannot reach must never read as available. */
function ContractRow({ option, now }: { option: MuAuctionOption; now: number }) {
  const secondsLeft =
    option.closesAtMs === null ? null : Math.max(0, Math.floor((option.closesAtMs - now) / 1000));
  const clock = countdownWords(secondsLeft, option.extendable);
  return (
    <li className="tg-contract" data-phase={option.phase} data-barred={option.canBid ? undefined : "yes"}>
      <div className="tg-contract-head">
        {/* The loudest thing on an open auction. These run five minutes at their
            shortest, so the clock outranks every other fact on the row. */}
        <span className={`tg-clock${clock.urgent ? " urgent" : ""}`}>{clock.text}</span>
        <span className="tg-contract-ask">{compactDamage(option.expectedDamage)}</span>
        <span className="tg-contract-rate">{rateWords(option.ratePerK)}</span>
      </div>
      <div className="tg-contract-meta">
        <span>{option.poster ?? "poster unknown"}</span>
        <span>
          {option.payout === null
            ? WORDS.none
            : `${moneyWords(option.payout)} · ${
                option.deposit === null ? WORDS.nothingStaked : `${moneyWords(option.deposit)} staked`
              }`}
        </span>
        {option.professionalsOnly ? <span className="tg-pro">professionals only</span> : null}
        {option.canBid ? null : <span className="tg-barred">{option.bidReason}</span>}
      </div>
      {clock.note ? <p className="tg-note">{clock.note}</p> : null}
    </li>
  );
}

/**
 * What the contract asks for and what it pays, read-only.
 *
 * Five facts the unit decides on, in the design system's order. The deposit is
 * on screen because it is what the unit stands to lose, so where it stands is
 * the point of it, and a hand-set target says outright that nothing is staked.
 */
function TermsStrip({
  state,
  listed,
  held,
}: {
  state: MuState;
  listed: MuAuctionOption[];
  held: boolean;
}) {
  const ours = listed.find((o) => o.phase === "fulfilling") ?? listed[0] ?? null;
  const handSet = !held && !ours;
  const terms = contractTerms({
    handSet,
    expected: ours
      ? ours.expectedDamage
      : (state.contract?.targetDamage ?? state.handTarget ?? 0),
    ratePerK: ours?.ratePerK ?? null,
    payout: ours?.payout ?? null,
    deposit: ours?.deposit ?? null,
    // `settled` is our one word for every ending that is nobody's defeat, and
    // the strip wants to know which ending it was. Only the game can say, so a
    // settled auction reads as completed when the target was met and failed
    // when it was not.
    phase: ours
      ? ours.phase === "settled"
        ? state.progress?.reached
          ? "completed"
          : "failed"
        : ours.phase
      : held
        ? "fulfilling"
        : "none",
  });
  const bonus =
    state.battleBonus && state.battleBonus.source !== "none"
      ? percentWords(state.battleBonus.factor)
      : WORDS.bonusUnknown;
  const rows: [string, string, string?][] = [
    [terms.expectedLabel, terms.expected],
    // The bonus belongs to the battle, so every contract in it inherits it and
    // it sits here rather than in the header.
    ["battle bonus", bonus],
    ["rate", terms.rate],
    ["total payment", terms.payment],
    ["deposit", terms.deposit, terms.depositState],
  ];
  return (
    <dl className="terms">
      {rows.map(([label, value, state2]) => (
        <div className="terms-row" key={label}>
          <dt>{label}</dt>
          <dd>
            {value}
            {state2 ? <span className="terms-state">{state2}</span> : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Battle first, then contract.
 *
 * The reader aims the unit at a fight and the paperwork open in it is listed
 * underneath (spec ruling 5). Only an explicit pick posts the target: page
 * navigation never does, because one member browsing battles would re-point the
 * whole unit and two members on different pages would fight under
 * last-write-wins (ruling 7).
 */
function Targeting({
  state,
  me,
  now,
  busy,
  onPick,
  onHandTarget,
}: {
  state: MuState;
  me: string;
  now: number;
  busy: boolean;
  onPick: (battleId: string) => void;
  onHandTarget: (battleId: string, expectedDamage: number) => void;
}) {
  const [settled, setSettled] = useState(false);
  const [typing, setTyping] = useState(false);

  const target = state.target;
  const battles = state.battles ?? [];
  const setter = target?.setBy
    ? (state.roster.find((m) => m.userId === target.setBy)?.username ?? null)
    : null;

  const listed = target ? (state.contracts?.[target.battleId] ?? []) : [];
  // A battle the unit already holds a contract in stops offering the others:
  // it cannot take a second one there, so listing them offers work that does
  // not exist. The worker already collapsed the list; this says why.
  const held = listed.some((o) => o.phase === "fulfilling");
  const shown = settled
    ? listed
    : listed.filter((o) => o.phase !== "settled" && o.phase !== "lost");

  return (
    <section className="tg">
      <div className="tg-head">
        <span className="tg-label">Aimed at</span>
        {target ? (
          <span className="tg-source">
            {target.source === "page"
              ? "read off a member's page"
              : setter
                ? `set by ${setter} ${ageWords(now - target.setAtMs)}`
                : `set ${ageWords(now - target.setAtMs)}`}
          </span>
        ) : (
          <span className="tg-source">nothing yet</span>
        )}
      </div>

      {/* Attribution, not authentication: MU writes are open, so this is who
          claims to have set it and nothing here may imply it was proved. */}
      <ul className="tg-battles">
        {battles.length === 0 ? (
          <li className="tg-empty">No battle is offering a contract right now.</li>
        ) : (
          battles.map((battle) => (
            <BattleRow
              key={battle.battleId}
              battle={battle}
              targeted={target?.battleId === battle.battleId}
              busy={busy}
              onPick={() => onPick(battle.battleId)}
            />
          ))
        )}
      </ul>

      {target ? (
        <>
          {/* The terms, read-only, in one strip: everything here comes from the
              game and none of it can be typed over. On the board they stack as
              label/value rows rather than sitting side by side. */}
          <TermsStrip state={state} listed={listed} held={held} />

          <div className="tg-head">
            <span className="tg-label">{held ? "Holding" : "Open here"}</span>
            <label className="tg-settled">
              <input
                type="checkbox"
                checked={settled}
                onChange={(ev) => setSettled(ev.currentTarget.checked)}
              />
              show settled
            </label>
          </div>

          {held ? (
            <p className="tg-note">
              This unit holds a contract in this battle, so the others are not listed: it cannot
              take a second one here.
            </p>
          ) : null}

          <ul className="tg-contracts">
            {shown.length === 0 ? (
              <li className="tg-empty">
                {/* The standing-queue wording. A hand-set target borrows the
                    maths of an open auction and none of its copy. */}
                {`queue with no contract · set a target by hand`}
              </li>
            ) : (
              shown.map((option) => (
                <ContractRow key={option.auctionId} option={option} now={now} />
              ))
            )}
          </ul>

          {!held ? (
            <p className="tg-hand">
              <span className="tg-label">judged against</span>
              {typing ? (
                <input
                  className="lg-input"
                  type="number"
                  min={0}
                  step={100000}
                  autoFocus
                  defaultValue={state.handTarget ?? 1_000_000}
                  aria-label="Damage to judge the queue against"
                  onKeyDown={(ev) => {
                    if (ev.key === "Escape") setTyping(false);
                    if (ev.key === "Enter") ev.currentTarget.blur();
                  }}
                  onBlur={(ev) => {
                    const value = Math.round(Number(ev.currentTarget.value));
                    setTyping(false);
                    if (Number.isFinite(value) && value > 0) {
                      onHandTarget(target.battleId, value);
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="lg-figure handset"
                  disabled={busy}
                  onClick={() => setTyping(true)}
                >
                  {state.handTarget === null ? "set a target" : compactDamage(state.handTarget)}
                </button>
              )}
              <span className="tg-note">{WORDS.noRate}</span>
            </p>
          ) : null}
        </>
      ) : null}

      <p className="tg-note">
        {/* Ruling 7, said out loud. The board cannot read a game page at all;
            only the extension can, and even there it is off by default. */}
        Picking a battle points the whole unit at it. Nothing here follows the page you happen to
        be on. {me ? "" : ""}
      </p>
    </section>
  );
}

/* ---------------------------------------------------------------- the ledger */

/** The gear a member is wearing: six ticks in slot order, as a button, with an
 * aria label that says what it is. Context on who is equipped for what, never a
 * damage rule — the damage figures already include every contribution gear
 * makes, and nothing in the game caps a set at its weakest slot. */
function GearStrip({
  row,
  open,
  onToggle,
  onHover,
}: {
  row: LedgerRow;
  open: boolean;
  onToggle: () => void;
  onHover: (open: boolean) => void;
}) {
  if (row.gear === null) {
    return (
      <span className="gear gear-unknown" title={`The game would not say what ${row.username} is wearing`}>
        {GEAR_SLOTS.map((slot) => (
          <span key={slot} className="gear-tick gear-none" />
        ))}
      </span>
    );
  }
  const bySlot = new Map(row.gear.map((g) => [g.slot, g]));
  const worn = row.gear.filter((g) => g.nearlyBroken).length;
  return (
    <button
      type="button"
      className="gear"
      aria-expanded={open}
      aria-label={`${row.username}'s gear, ${row.gear.length} of six slots${worn > 0 ? `, ${worn} nearly worn out` : ""}`}
      // A pointer only has to rest on the strip; a thumb still taps, and a
      // keyboard still gets it on focus. The card is an overlay either way, so
      // reading somebody's gear never moves the rows under the pointer.
      onClick={onToggle}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      onFocus={() => onHover(true)}
      onBlur={() => onHover(false)}
    >
      {GEAR_SLOTS.map((slot) => {
        const item = bySlot.get(slot);
        return (
          <span
            key={slot}
            // A rarity the config did not name is not "common": guessing the
            // bottom of the ramp paints a mythic weapon grey and reads as a
            // fact. The index is zero-based, so `?? 0` said exactly that.
            className={`gear-tick ${item ? `gear-r${item.rarity ?? "unknown"}` : "gear-none"}${item?.nearlyBroken ? " gear-worn" : ""}`}
          />
        );
      })}
    </button>
  );
}

/** What the strip's card says: one line per slot, its tier and how much of it is
 * left. A slot near zero is about to stop contributing, which is the one thing
 * here a queue planner can act on. */
function GearCard({ row }: { row: LedgerRow }) {
  // An overlay, never a taller row: the card opens over what is beside it, so
  // reading one member's gear never pushes the rows under the pointer down. It
  // flips above the row when the bottom of the window would clip it.
  const box = useRef<HTMLDivElement | null>(null);
  const [flip, setFlip] = useState(false);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setFlip(rect.bottom > window.innerHeight - 8);
  }, [row.userId]);
  const body =
    row.gear === null ? (
      <p className="gearcard-empty">The game would not say what they are wearing.</p>
    ) : row.gear.length === 0 ? (
      <p className="gearcard-empty">Carrying nothing.</p>
    ) : null;
  if (body) {
    return (
      <div className="gearcard" ref={box} data-flip={flip ? "up" : undefined} role="tooltip">
        {body}
      </div>
    );
  }
  const bySlot = new Map(row.gear!.map((g) => [g.slot, g]));
  return (
    <div className="gearcard" ref={box} data-flip={flip ? "up" : undefined} role="tooltip">
      {GEAR_SLOTS.map((slot) => {
        const item = bySlot.get(slot);
        return (
          <p key={slot} className={`gearcard-row${item ? "" : " gearcard-empty-slot"}`}>
            <span>{slot}</span>
            <span className={item ? "gearcard-tier" : undefined}>
              {item ? (item.rarityName ?? "unknown tier") : "empty"}
            </span>
            <span className={item?.nearlyBroken ? "gearcard-worn" : undefined}>
              {item
                ? item.durability === null
                  ? "durability unknown"
                  : `${Math.round(item.durability * 100)}% left`
                : ""}
            </span>
          </p>
        );
      })}
    </div>
  );
}

/** The pill and the penalty, figure and countdown drawn apart so a narrow card
 * can clip the time and never the number. */
function EffectChip({ effect, kind }: { effect: Effect | null; kind: "pill" | "pen" }) {
  if (!effect) return null;
  return (
    <span
      className={`lg-eff lg-eff-${kind}${effect.expiringSoon ? " lg-eff-soon" : ""}`}
      title={`${effect.name}${effect.left ? `, ${effect.left} left` : ""}`}
    >
      <span className="lg-eff-fig">{effect.figure}</span>
      {effect.left ? <span className="lg-eff-left">{effect.left}</span> : null}
    </span>
  );
}

/**
 * The ledger, as the board draws it: two lines per member instead of seven
 * columns, carrying the same facts in the same order.
 *
 * Same model as the panel (`muLedger.ts`), so the two surfaces cannot group
 * members differently, count them differently, or disagree about what is left
 * to deal. Every interactive element here clears 44px, because this is the
 * surface that gets read one-handed mid-fight.
 */
function QueueLedger({
  state,
  me,
  now,
  busy,
  onSet,
  onDrop,
  onCopy,
}: {
  state: MuState;
  me: string;
  now: number;
  busy: boolean;
  onSet: (userId: string, username: string, pledge: number | null) => void;
  onDrop: (userId: string) => void;
  onCopy: () => void;
}) {
  const [sort, setSort] = useState<SortKey | null>(null);
  const [gearFor, setGearFor] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  /** Whose removal is asked about. Taking somebody off a queue the unit is
   * counting on is the one action here that costs the unit damage, so it names
   * what leaves and what that does to the shortfall. */
  const [dropping, setDropping] = useState<string | null>(null);
  /** A figure typed over somebody else's own stated figure, waiting to be
   * confirmed. Not destructive in the delete sense, but it overwrites a promise
   * that member made, and confirmation is the only guard this product has. */
  const [pendingSet, setPendingSet] = useState<{ row: LedgerRow; value: number } | null>(null);

  useEffect(() => {
    if (dropping === null) return;
    const timer = setTimeout(() => setDropping(null), ARM_MS);
    return () => clearTimeout(timer);
  }, [dropping]);

  const built = useMemo(
    () => ledger(state, me, now, { sort: sort ?? undefined }),
    [state, me, now, sort],
  );

  const heads: { label: string; key: SortKey }[] = [
    { label: "member", key: "member" },
    { label: "pill", key: "pill" },
    { label: "dealt", key: "dealt" },
    // Before the award every dealt figure is zero, so there is nothing for
    // anything to be left of: the queue is capacity, and the word is committed.
    { label: built.preAward ? WORDS.committed : WORDS.leftToDeal, key: "left" },
  ];

  return (
    <section className="lg">
      {/* Taking the queue to the channel is an occasional act about the whole
          list, so it belongs to the list rather than to whichever member is
          reading. On a phone the pinned block still carries it. */}
      <div className="lg-tools">
        <button type="button" className="ghost lg-copy" onClick={onCopy}>
          Copy for Discord
        </button>
      </div>
      <div className="lg-sorts" role="group" aria-label="Sort the ledger">
        {heads.map((h) => (
          <button
            key={h.key}
            type="button"
            className={`lg-sort${sort === h.key ? " on" : ""}`}
            aria-pressed={sort === h.key}
            // Pressing the one already on turns it off rather than flipping to
            // ascending. Off is a real answer: it is the queue's own order.
            onClick={() => setSort(sort === h.key ? null : h.key)}
          >
            {h.label}
          </button>
        ))}
      </div>

      {built.groups.map((group) => (
        <div className="lg-group" data-group={group.id} key={group.id}>
          {/* The head names the group and nothing else: a committed figure over
              the group nobody promised anything from would be the same lie in
              two places. The figures sit in the band under the rows. */}
          <div className="lg-grouphead" data-group={group.id}>
            <span>{groupHeadWords(group.id, group.count)}</span>
          </div>

          {group.rows.length === 0 ? (
            <p className="lg-empty">
              {group.id === "in" ? "Nobody is in the queue." : "Everybody is in."}
            </p>
          ) : (
            <ul>
              {group.rows.map((row) => {
                const pay = payoutWords(built.phase, row.payout);
                // One question, asked in two places at once: the words on the
                // buttons sit in the row, the question itself under it.
                const dropWords = removeMemberConfirm({
                  username: row.username,
                  committed: row.committed,
                  delivered: row.dealt,
                  pledged: row.handSet,
                  addedByTeammate: null,
                  live: built.phase === "fulfilling",
                });
                return (
                  <li key={row.userId} className={row.isMe ? "is-me" : undefined}>
                    {/* Line one: who, and what is true of the person. */}
                    <div className="lg-line lg-line-who" title={capacityWords(row)}>
                      <MuFace
                        username={row.username}
                        avatarUrl={row.avatarUrl}
                        online={row.online}
                        size="sm"
                      />
                      {/* The level token: about the player, not about what they
                          are called, and absent rather than zero when the game
                          did not say. */}
                      <span className="lg-name">{row.username}</span>
                      {row.level === null ? null : (
                        <span className="lg-level" title={`Level ${row.level}`}>
                          L{row.level}
                        </span>
                      )}
                      {row.known ? null : <span className="lg-tag">gone</span>}
                      {row.alreadyHitThisRound ? <span className="lg-tag">hit this round</span> : null}
                      <GearStrip
                        row={row}
                        open={gearFor === row.userId}
                        onToggle={() => setGearFor(gearFor === row.userId ? null : row.userId)}
                        onHover={(on) => setGearFor(on ? row.userId : null)}
                      />
                      <EffectChip effect={row.pill} kind="pill" />
                      <EffectChip effect={row.penalty} kind="pen" />
                      {!row.pill && !row.penalty ? (
                        <span className="lg-eff lg-eff-none">{WORDS.noPill}</span>
                      ) : null}
                    </div>

                    {/* Line two: the figures, in the panel's column order. */}
                    <div className="lg-line lg-line-figures">
                      {/* Solid happened, hatched was promised, dashed is
                          merely possible: the same three materials the bar
                          uses, on the same scale. */}
                      <span className="lg-share" aria-hidden="true">
                        <span
                          className="lg-share-fill"
                          style={{ width: `${Math.max(0, Math.min(100, row.sharePct)).toFixed(1)}%` }}
                        />
                        <span
                          className={`lg-share-rest ${row.inQueue ? "owed" : "free"}`}
                          style={{
                            width: `${Math.max(
                              0,
                              Math.min(100 - Math.min(100, row.sharePct), row.owedPct),
                            ).toFixed(1)}%`,
                          }}
                        />
                      </span>
                      <span className="lg-dealt" title={`${exactDamage(row.dealt)} dealt`}>
                        {row.dealt > 0 ? compactDamage(row.dealt) : WORDS.none}
                      </span>
                      {/* Gallery order: dealt, would earn, then the editable
                          figure. */}
                      <span className={`lg-pay lg-pay-${pay.tone}`}>{pay.text}</span>
                      {editing === row.userId ? (
                        <input
                          className="lg-input"
                          type="number"
                          min={0}
                          step={1000}
                          autoFocus
                          defaultValue={Math.round(row.leftToDeal)}
                          aria-label={`Damage left to deal for ${row.username}`}
                          onKeyDown={(ev) => {
                            if (ev.key === "Escape") {
                              ev.preventDefault();
                              setEditing(null);
                            }
                            if (ev.key === "Enter") ev.currentTarget.blur();
                          }}
                          onBlur={(ev) => {
                            const value = Math.round(Number(ev.currentTarget.value));
                            setEditing(null);
                            if (!Number.isFinite(value) || value < 0) return;
                            // Overwriting a figure somebody set for themselves
                            // is a claim about their promise, so it asks first.
                            if (row.userId !== me && row.handSet) {
                              setPendingSet({ row, value });
                              return;
                            }
                            // Typing what a member will deal IS saying they are
                            // in, so editing somebody outside the queue puts
                            // them in it rather than losing the figure.
                            onSet(row.userId, row.username, value);
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          className={`lg-figure ${built.preAward ? "plan" : row.leftToDeal > 0 ? "owing" : "clear"}${row.handSet ? " handset" : ""}`}
                          disabled={busy}
                          // The dot says "somebody typed this" to anyone who
                          // knows the convention. The words say it to everyone
                          // else, including a screen reader.
                          title={commitKindWords(row.handSet, row.committed)}
                          aria-label={`Change what ${row.username} is in for. ${commitmentWords(row, exactDamage)}. ${commitKindWords(row.handSet, row.committed)}`}
                          onClick={() => setEditing(row.userId)}
                        >
                          {compactDamage(row.leftToDeal)}
                          {row.handSet ? <span className="lg-dot" aria-hidden="true" /> : null}
                        </button>
                      )}
                      {dropping === row.userId ? (
                        // The answer lands where the question was asked. The
                        // \u00d7 becomes the button that goes through with it, so
                        // the hand does not travel to a strip on the line
                        // below to finish what this cell started.
                        <span className="lg-answers">
                          <button
                            type="button"
                            className="lg-answer no"
                            onClick={() => setDropping(null)}
                          >
                            {dropWords.no}
                          </button>
                          <button
                            type="button"
                            className="lg-answer yes"
                            disabled={busy}
                            onClick={() => {
                              setDropping(null);
                              onDrop(row.userId);
                            }}
                          >
                            {dropWords.yes}
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className={`lg-toggle ${row.inQueue ? "out" : "in"}`}
                          disabled={busy}
                          aria-label={
                            row.inQueue
                              ? `Take ${row.username} out of the queue`
                              : `Put ${row.username} in the queue`
                          }
                          onClick={() =>
                            row.inQueue
                              ? setDropping(row.userId)
                              : // Null, not their projection: putting somebody in
                                // states that they are available, not that they
                                // promised a fixed figure.
                                onSet(row.userId, row.username, null)
                          }
                        >
                          {row.inQueue ? "\u00d7" : "+"}
                        </button>
                      )}
                    </div>

                    {gearFor === row.userId ? <GearCard row={row} /> : null}

                    {pendingSet?.row.userId === row.userId ? (
                      <Confirm
                        // The words on the buttons travel with the question
                        // now: both surfaces ask it, so both surfaces have to
                        // offer the same two answers.
                        {...setOthersCommitmentConfirm(
                          row.username,
                          pendingSet.value,
                          row.committed,
                          row.handSet,
                        )}
                        busy={busy}
                        onYes={() => {
                          const { value } = pendingSet;
                          setPendingSet(null);
                          onSet(row.userId, row.username, value);
                        }}
                        onNo={() => setPendingSet(null)}
                      />
                    ) : null}

                    {dropping === row.userId ? (
                      // Only the question: its two answers are up in the row,
                      // on the cell the reader just clicked.
                      <p className="board-confirm asked">{dropWords.question}</p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}

          {/* The band that totals the group it sits under, in the group's own
              terms: what it dealt, and what it still owes. */}
          {(() => {
            const total = subtotalWords({
              id: group.id,
              count: group.count,
              dealt: group.dealt,
              leftToDeal: group.leftToDeal,
              committed: group.committed,
              preAward: built.preAward,
              closed: built.phase === "settled" || built.phase === "lost",
            });
            return (
              <p className="lg-total" data-group={group.id}>
                <span>{total.head}</span>
                <span>{total.figure}</span>
              </p>
            );
          })()}
        </div>
      ))}

      {/* The whole unit closes the ledger: everybody, queued or not. */}
      {(() => {
        const all = unitRowWords({
          count: built.unit.count,
          capacity: built.unit.committed,
          dealt: built.unit.dealt,
        });
        return (
          <p className="lg-total lg-unit">
            <span>{all.head}</span>
            <span>{all.figure}</span>
          </p>
        );
      })()}
    </section>
  );
}



/* ------------------------------------------------------------ commit bar */

function CommitBar({
  busy,
  live,
  inQueue,
  mine,
  shortfall,
  committed,
  dealt,
  committedIsFixed,
  pending,
  onPending,
  onJoin,
  onLeave,
  onCopy,
}: {
  busy: boolean;
  /** Whether the unit actually holds a contract. Without one, joining is a
   * standing offer to fight the next one rather than a commitment to this. */
  live: boolean;
  inQueue: boolean;
  mine: MuRosterMember | undefined;
  shortfall: number;
  committed: number;
  /** What this member has already put on the board, which the design's own line
   * states beside what they signed up for. */
  dealt: number;
  /** Whether what this member committed is a figure they typed, or their live
   * projection. The same number means two different things. */
  committedIsFixed: boolean;
  pending: number | null;
  onPending: (value: number | null) => void;
  onJoin: (pledge: number | null) => void;
  onLeave: () => void;
  onCopy: () => void;
}) {
  // A real prefilled value, not a placeholder. A placeholder is not a default,
  // and an untouched field used to join with null with that behaviour explained
  // nowhere.
  const projection = Math.round(mine?.projectedDamage ?? 0);
  const [pledge, setPledge] = useState(String(projection || ""));
  const [touched, setTouched] = useState(false);
  const [leaving, setLeaving] = useState(false);
  /** Which kind of commitment this is. Following is the default because it is
   * what most members want and what the queue already did when left alone. */
  const [mode, setMode] = useState<"following" | "fixed">(
    committedIsFixed ? "fixed" : "following",
  );
  // What this member is carrying, beside the figure it moves: the design puts
  // the pill on the ring's own line rather than only in their ledger row.
  const pill = mine ? pillLabel(mine, Date.now()) : null;
  const penalty = mine ? debuffLabel(mine, Date.now()) : null;

  useEffect(() => {
    // While following, the field is a readout of what this member is currently
    // worth, so it keeps up with the roster. A fixed figure is theirs and is
    // never overwritten underneath them.
    if (!touched && mode === "following") setPledge(String(projection || ""));
  }, [projection, touched, mode]);

  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(() => setLeaving(false), ARM_MS);
    return () => clearTimeout(timer);
  }, [leaving]);

  // The bar is fixed to the bottom, so the page has to reserve exactly its
  // height or the last thing on the page sits underneath it. That reserve used
  // to be a hardcoded 88px, which was the height of one row of buttons on one
  // phone: stacked on a narrow screen, or with the reader's font size turned
  // up, the bar grew and swallowed the queue and the last contract.
  const barRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const measure = () =>
      document.documentElement.style.setProperty("--commit-h", `${el.offsetHeight}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--commit-h");
    };
  });

  const typed = Number(pledge.replace(/[\s,_]/g, ""));
  const value = Number.isFinite(typed) && typed > 0 ? Math.round(typed) : null;
  const after = value === null ? shortfall : Math.max(0, shortfall - value);

  return (
    <div className="commit" ref={barRef}>
      <div className="commit-inner commit-stack">
        {/* Where the reader stands, in the block that changes it — the design's
            own arrangement. A headline card above the progress said the same
            figure a second time and the verdict chip a third. */}
        <div className="commit-who">
          <MuFace
            username={mine?.username ?? "you"}
            avatarUrl={mine?.avatarUrl}
            online={mine?.online}
            size="lg"
            level={typeof mine?.level === "number" ? mine.level : null}
          />
          <span className="commit-own">
            {ownStandingLine({
              username: mine?.username ?? "you",
              inQueue,
              committed,
              dealt,
            })}
          </span>
          {pill ? <EffectChip effect={pill} kind="pill" /> : null}
          {penalty ? <EffectChip effect={penalty} kind="pen" /> : null}
        </div>
        {leaving ? (
          <div className="commit-row">
            <span className="commit-note">
              {removeMemberConfirm({ isMe: true, live, committed, shortfall }).question}
            </span>
            <button type="button" className="ghost" disabled={busy} onClick={onLeave}>
              {live ? "Yes, leave" : "Yes"}
            </button>
            <button type="button" className="ghost" onClick={() => setLeaving(false)}>
              {live ? "Stay" : "Stay on it"}
            </button>
          </div>
        ) : null}
        {/* Three shares of what this member can actually do. There is no
            preset above all of it: over-committing is done by typing a number,
            which food and pills make legitimate. */}
        <div className="commit-presets">
          {COMMIT_PRESETS.map((share) => {
            const value = Math.round((projection * share) / 1000) * 1000;
            return (
              <button
                key={share}
                type="button"
                className="commit-preset"
                onClick={() => {
                  // A percentage keeps tracking capacity, so it is the
                  // following kind however it was reached.
                  setMode(share === 1 ? "following" : "fixed");
                  setTouched(share !== 1);
                  setPledge(String(value));
                }}
              >
                {presetLabel(share, value)}
              </button>
            );
          })}
        </div>
        <span className="commit-word" data-tracking={mode === "following" ? "yes" : "no"}>
          {commitWord(mode === "following")}
        </span>
        <div className="commit-row">
          <input
            type="text"
            inputMode="numeric"
            value={pledge}
            data-tracking={mode === "following" ? "yes" : "no"}
            readOnly={mode === "following"}
            aria-label={
              mode === "following"
                ? "What your health and food currently afford. The unit reads this figure again every poll."
                : "The figure you are committing to, held whatever your health does."
            }
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
              // Typing a figure states that you mean that figure. It selects
              // the mode rather than silently changing what the button does.
              setMode("fixed");
              setTouched(true);
              setPledge(e.target.value);
            }}
          />
          <button
            type="button"
            disabled={busy || value === null}
            onClick={() => {
              onPending(mode === "fixed" ? value : null);
              // The kind is the member's own choice now, not an inference from
              // whether they touched the field. Following sends null, which is
              // what makes the queue keep reading their live projection.
              onJoin(mode === "fixed" ? value : null);
            }}
          >
            {busy && pending !== null ? "Saving…" : signLabel(inQueue)}
          </button>
          {inQueue ? (
            <>
              <button type="button" className="ghost" onClick={onCopy}>
                Copy for Discord
              </button>
              {/* The quietest thing in the block. Withdrawing is not what a
                  member opens the board to do. */}
              <button
                type="button"
                className="ghost quiet"
                disabled={busy}
                onClick={() => setLeaving(true)}
              >
                withdraw
              </button>
            </>
          ) : null}
        </div>
        {/* Why the figure is what it is, and whether it will move on its own:
            a percentage keeps tracking capacity, a typed number stays put. The
            panel has printed this all along; the board never did. */}
        <span className="commit-note">
          {affordLine({
            hits: Math.round(mine?.hitsCapacity ?? 0),
            perHit: mine?.damagePerHit ?? 0,
            affords: projection,
            tracking: mode === "following",
          })}
        </span>
        {live ? (
          <span className="commit-note">
            {after > 0 ? `Leaves the unit ${exactDamage(after)} short.` : "That covers the unit."}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- history */

/**
 * The last contract, and what it paid this member.
 *
 * Nobody opens the game to check payouts; they remember hours later and land on
 * whichever surface is nearer, which on the idle screen is this one. A fold is
 * the wrong shape for a question somebody arrived with, so on the idle screen it
 * is one line and the split stays behind the fold.
 */
function LastContract({
  muId,
  muName,
  me,
  roster,
  neverRelayed,
  idle,
}: {
  muId: string;
  muName: string;
  me: string;
  roster: MuRosterMember[];
  neverRelayed: boolean;
  idle: boolean;
}) {
  const [contracts, setContracts] = useState<MuFeedContract[] | null>(null);
  const [failed, setFailed] = useState(false);
  // Open. This is the whole of the History screen, and a section you must open
  // to see the only thing on the screen is a door in front of a door. It was a
  // fold when it shared a column with the live contract and had to get out of
  // its way.
  const [open, setOpen] = useState(true);
  const names = useMemo(() => new Map(roster.map((m) => [m.userId, m.username])), [roster]);

  const load = useCallback(() => {
    if (contracts || failed) return;
    muApi
      .contracts(muId)
      .then((data) => setContracts(data.contracts))
      .catch(() => setFailed(true));
  }, [contracts, failed, muId]);

  // On the idle screen the summary line IS the answer, so it cannot wait for a
  // tap it was never going to get.
  useEffect(() => {
    if (idle) load();
  }, [idle, load]);

  if (neverRelayed) {
    return (
      <section className="board-last">
        <p className="board-hint">
          <b>Last contract</b> Not recorded. Payouts and the per-member split only exist if a member
          of your unit runs the extension. Nobody in {muName} has.
        </p>
      </section>
    );
  }

  const newest = contracts?.[0] ?? null;
  const myPay = newest ? (newest.moneyPerUser[me] ?? 0) : 0;

  return (
    <section className="board-last">
      {idle && newest ? (
        <p className="board-last-line">
          <b>Last contract</b> {compactDamage(newest.targetDamage)}, {newest.status}{" "}
          {newest.completedAt || newest.wonAt
            ? ageWords(Date.now() - Date.parse((newest.completedAt ?? newest.wonAt)!))
            : ""}
          . {myPay > 0 ? `You earned ${money(myPay)}.` : "You earned nothing from it."}{" "}
          <button type="button" className="linkish" onClick={() => setOpen(!open)}>
            {open ? "Hide the split" : "See the split"}
          </button>
        </p>
      ) : null}

      <details
        className="board-fold"
        open={open || undefined}
        onToggle={(e) => {
          load();
          setOpen((e.target as HTMLDetailsElement).open);
        }}
      >
        <summary>Past contracts</summary>
        {failed ? <p className="board-hint">Could not load them.</p> : null}
        {contracts === null && !failed ? <p className="board-hint">Reading the record&hellip;</p> : null}
        {contracts !== null && contracts.length === 0 ? (
          <p className="board-hint">
            Nothing yet. Every contract the unit wins is kept here with who dealt what and what the
            game paid them.
          </p>
        ) : null}
        {(contracts ?? []).map((c) => {
          const split = Object.entries(c.damagesByUser).sort((a, b) => b[1] - a[1]);
          return (
            <div key={c.gameRef} className="board-contract">
              <div className="manifest-head">
                <span>{c.wonAt ? new Date(c.wonAt).toLocaleDateString() : "contract"}</span>
                <span>
                  {compactDamage(c.currentDamage)} / {compactDamage(c.targetDamage)} · {c.status}
                </span>
              </div>
              <ul className="board-discord">
                {split.map(([userId, damage]) => (
                  <li key={userId}>
                    <span className="row-name">{names.get(userId) ?? "left the unit"}</span>
                    <span className="row-figure">
                      {compactDamage(damage)}
                      {c.moneyPerUser[userId] ? (
                        <em className="row-pay">{money(c.moneyPerUser[userId])}</em>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
              {split.length === 0 ? (
                <p className="board-hint">The game recorded no split for this contract.</p>
              ) : null}
            </div>
          );
        })}
      </details>
    </section>
  );
}

/* --------------------------------------------------------------- discord */

/**
 * Who each member is on Discord, so the message somebody pastes into the unit's
 * channel mentions them for real. A name written as text renders like a tag and
 * notifies nobody; only the account id reaches a phone.
 */
function DiscordFold({
  busy,
  roster,
  onSet,
}: {
  busy: boolean;
  roster: MuState["roster"];
  onSet: (userId: string, discordId: string) => void;
}) {
  return (
    <details className="board-fold" open>
      <summary>Discord pings</summary>
      <ul className="board-discord">
        {roster.map((m) => (
          <li key={m.userId}>
            <span className="row-name">{m.username}</span>
            <input
              defaultValue={m.discordId ?? ""}
              disabled={busy}
              inputMode="numeric"
              placeholder="Discord id"
              aria-label={`Discord id for ${m.username}`}
              onBlur={(e) => {
                const next = e.target.value.trim();
                if (next === (m.discordId ?? "")) return;
                onSet(m.userId, next);
              }}
            />
          </li>
        ))}
      </ul>
      <p className="board-hint">
        Send <code>\@yourname</code> in any Discord channel: it posts your id as
        <code>&lt;@1234567890&gt;</code>. Paste that here. Empty the field to remove it. These are
        shared with the panel.
      </p>
    </details>
  );
}

/* ------------------------------------------------------------------ plan */

/**
 * Planning, or the one number the game never states.
 *
 * Three phases and one card. While the unit is underwriting, the bonus is an
 * assumption and the control is an input. Once a contract is live but nothing
 * has been measured, it is still an assumption and says so. Once members have
 * actually hit, it is a measured fact and the control becomes a readout: there
 * is nothing left to set, which is a consequence of live data beating our
 * assumptions rather than a missing feature.
 */
function PlanFold({
  busy,
  live,
  me,
  state,
  bonusAuthor,
  now,
  onSet,
  onSetBonus,
  onClear,
}: {
  busy: boolean;
  /** Whether the unit holds a contract the game named. */
  live: boolean;
  me: string;
  state: MuState;
  bonusAuthor: BonusAuthor;
  now: number;
  onSet: (battleId: string, target: number, bonus: number | null) => void;
  onSetBonus: (bonus: number | null) => void;
  onClear: () => void;
}) {
  const [battle, setBattle] = useState(state.contract?.battleId ?? "");
  const [target, setTarget] = useState(state.contract ? String(state.contract.targetDamage) : "");
  const [bonus, setBonus] = useState(
    state.contract?.bonusPercent === null || state.contract?.bonusPercent === undefined
      ? ""
      : String(state.contract.bonusPercent),
  );
  const [confirm, setConfirm] = useState<null | "bonus" | "clearBonus" | "target" | "clearTarget">(null);

  useEffect(() => {
    if (confirm === null) return;
    const timer = setTimeout(() => setConfirm(null), ARM_MS);
    return () => clearTimeout(timer);
  }, [confirm]);

  const readBonus = (): number | null => {
    const percent = Number(bonus);
    return bonus.trim() && Number.isFinite(percent) && percent > -100 ? percent : null;
  };

  const measured = state.battleBonus.source === "measured";
  const setFactor = state.battleBonus.setFactor;

  // Phase 3: a measured fact, so a readout. No second control appears from
  // nowhere and nothing here offers to overwrite a measurement.
  if (live && measured) {
    return (
      <details className="board-fold" open>
        <summary>Battle bonus</summary>
        <p className="ledger-verdict">Battle bonus, {percentWords(state.battleBonus.factor)}</p>
        <p className="board-hint">
          Measured from what {state.battleBonus.sampleSize}{" "}
          {state.battleBonus.sampleSize === 1 ? "member" : "members"} actually dealt. Every
          projection here uses it.
        </p>
        {setFactor !== null ? (
          <p className="board-hint">
            {bonusSupersededLine(setFactor, state.battleBonus.factor, bonusAuthor, now)}
          </p>
        ) : null}
        {/* Ruling: no reachable control demotes a game-named contract, on either
            surface. The board used to offer "Clear contract" right here. */}
        <p className="board-hint">The game ends this contract, not the unit.</p>
      </details>
    );
  }

  // Phase 2: live, nothing measured yet. Still an assumption, now load-bearing.
  if (live) {
    return (
      <details className="board-fold" open>
        <summary>Battle bonus</summary>
        <p className="ledger-verdict">
          Battle bonus, {setFactor === null ? "not set" : percentWords(setFactor) + " (assumed)"}
        </p>
        <p className="board-hint">
          Set by hand. Nothing measured yet: projections are only as good as this figure. Read it
          off the battle page.
        </p>
        <label>
          <span>Battle bonus, in percent</span>
          <input
            value={bonus}
            onChange={(e) => setBonus(e.target.value)}
            inputMode="decimal"
            placeholder="read it off the battle page"
          />
        </label>
        {confirm === "bonus" && setFactor !== null ? (
          <Confirm
            {...replaceBonusConfirm(setFactor, readBonus() ?? 0, bonusAuthor, now)}
            busy={busy}
            onYes={() => {
              onSetBonus(readBonus());
              setConfirm(null);
            }}
            onNo={() => setConfirm(null)}
          />
        ) : confirm === "clearBonus" && setFactor !== null ? (
          <Confirm
            {...clearBonusConfirm(setFactor, bonusAuthor, now)}
            busy={busy}
            onYes={() => {
              onSetBonus(null);
              setConfirm(null);
            }}
            onNo={() => setConfirm(null)}
          />
        ) : (
          <div className="board-actions">
            <button
              type="button"
              className="act"
              disabled={busy}
              onClick={() => (setFactor === null ? onSetBonus(readBonus()) : setConfirm("bonus"))}
            >
              Save
            </button>
            {setFactor !== null ? (
              <button type="button" className="ghost" disabled={busy} onClick={() => setConfirm("clearBonus")}>
                Clear the bonus
              </button>
            ) : null}
          </div>
        )}
        <p className="board-hint">The game ends this contract, not the unit.</p>
      </details>
    );
  }

  const author = state.contract
    ? (state.roster.find((m) => m.userId === state.contract?.createdBy)?.username ?? null)
    : null;
  const isYou = state.contract?.createdBy === me;
  const wanted = Number(target.replace(/[\s,_]/g, ""));

  return (
    <details className="board-fold">
      <summary>Plan against a target</summary>
      <label>
        <span>Battle link or id</span>
        <input
          value={battle}
          onChange={(e) => setBattle(e.target.value)}
          placeholder="app.warera.io/battle/..."
        />
      </label>
      <label>
        <span>Damage to aim at</span>
        <input
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          inputMode="numeric"
          placeholder="1000000"
        />
      </label>
      <label>
        <span>Battle bonus, in percent</span>
        <input
          value={bonus}
          onChange={(e) => setBonus(e.target.value)}
          inputMode="decimal"
          placeholder="read it off the battle page"
        />
      </label>
      {confirm === "target" && state.contract ? (
        <Confirm
          question={replaceTargetConfirm(
            isYou ? "your" : author,
            state.contract.targetDamage,
            Math.round(wanted),
          )}
          yes="Yes, replace it"
          no="Cancel"
          busy={busy}
          onYes={() => {
            onSet(battle.trim(), Math.round(wanted), readBonus());
            setConfirm(null);
          }}
          onNo={() => setConfirm(null)}
        />
      ) : confirm === "clearTarget" && state.contract ? (
        <Confirm
          {...clearTargetConfirm(state.contract.targetDamage, author, isYou)}
          busy={busy}
          onYes={() => {
            onClear();
            setConfirm(null);
          }}
          onNo={() => setConfirm(null)}
        />
      ) : (
        <div className="board-actions">
          <button
            type="button"
            className="act"
            disabled={busy}
            onClick={() => {
              if (!battle.trim() || !Number.isFinite(wanted) || wanted <= 0) return;
              // Overwriting a target somebody else set is not the same act as
              // setting the first one, and it says whose it is.
              if (state.contract && !isYou) setConfirm("target");
              else onSet(battle.trim(), Math.round(wanted), readBonus());
            }}
          >
            Set
          </button>
          {state.contract ? (
            <button type="button" className="ghost" disabled={busy} onClick={() => setConfirm("clearTarget")}>
              Clear
            </button>
          ) : null}
        </div>
      )}
      <p className="board-hint">
        A contract your unit wins arrives on its own. This is for working out whether the unit can
        cover one before it does.
      </p>
    </details>
  );
}

/* ------------------------------------------------------------------ link */

/**
 * The board's own link, and replacing it.
 *
 * The README has promised "rotate it from the board to revoke" since the link
 * existed, and the board had no link UI of any kind. It is a fold because it is
 * rare and destructive: replacing it kills the link every member holds,
 * including the page doing it.
 */
function LinkFold({
  muId,
  muName,
  url,
  busy,
}: {
  muId: string;
  muName: string;
  url: string;
  busy: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const [replaced, setReplaced] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!confirming) return;
    const timer = setTimeout(() => setConfirming(false), ARM_MS);
    return () => clearTimeout(timer);
  }, [confirming]);

  const shown = replaced ?? url;

  return (
    <details className="board-fold" open>
      <summary>This board&apos;s link</summary>
      <p className="board-link" onFocus={(e) => (e.target as HTMLElement).blur()}>
        <input readOnly value={shown} aria-label="Board link" onFocus={(e) => e.target.select()} />
        <button
          type="button"
          className="ghost"
          onClick={() => {
            navigator.clipboard
              .writeText(shown)
              .then(() => setCopied(true))
              .catch(() => setCopied(false));
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </p>
      {replaced ? (
        <p className="board-hint">Done. The old link is dead. Paste the new one in your channel.</p>
      ) : confirming ? (
        <Confirm
          question={replaceLinkConfirm(muName)}
          yes="Yes, replace it"
          no="Keep it"
          busy={busy}
          onYes={() => {
            setConfirming(false);
            void muApi
              .link(muId, true)
              .then((link) => {
                dropCachedState(muId);
                setReplaced(boardUrl(window.location.origin, link));
              })
              .catch(() => setReplaced(null));
          }}
          onNo={() => setConfirming(false)}
        />
      ) : (
        <div className="board-actions">
          <button type="button" className="ghost" onClick={() => setConfirming(true)}>
            Replace this link
          </button>
        </div>
      )}
      <p className="board-hint">
        Replacing it kills the link everyone in {muName} is using, including this page. Only do it
        if the link got out.
      </p>
    </details>
  );
}

/** Every confirmation names what dies and who set it. A generic "Are you sure?"
 * fails, and so does one that is still sitting there a minute later. */
function Confirm({
  question,
  yes,
  no,
  busy,
  onYes,
  onNo,
}: {
  question: string;
  yes: string;
  no: string;
  busy?: boolean;
  onYes: () => void;
  onNo: () => void;
}) {
  return (
    <p className="board-confirm">
      <span>{question}</span>
      <button type="button" className="danger" disabled={busy} onClick={onYes}>
        {yes}
      </button>
      <button type="button" className="ghost" onClick={onNo}>
        {no}
      </button>
    </p>
  );
}
